/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a store key, sort or status code; it renders nothing */
// The provisional first page (web instant Collection open, 2026-10-26).
//
// A cold /collection used to wait for boot, the capability probe and the
// first POST /copies/search before any row showed. This module keeps a SMALL
// owner-scoped record next to the A1 snapshot: the Cards list's first page
// for the default query (status tab All, no filter) under the device's sort,
// computed from the snapshot when it is stored and refreshed with it. The
// page paints it read-only while the live page is in the air; the live page
// replaces it wholesale and it is never written back or merged.
//
// Storage (C5): the owner's fenced database, store `collection`, key
// `first_page:<collection_id>`, written in the SAME fenced transaction as the
// snapshot body (snapshots.refreshCollectionSnapshot), so it is always the
// stored snapshot's page, is refused by a moved fence like the snapshot, and
// goes with the owner database on wipe and sign-out. Nothing in web storage.
//
// The record carries the owner incarnation and fence epoch it was written
// under; a read under any other (a cancelled transition adopted a new
// epoch, say) is refused, and a 304 rebuilds it under the current fence.
//
// Semantics: the server's own list order (mtg-api collection_copies
// `_sort_key`, the memory twin of the SQL keyset), whole key reversed for
// desc, copy_id the last key. Only the sorts the snapshot carries every key
// for are answered (added, name, value, set, gain, qty); mv / rarity / color
// / type rank the FRONT face through the server's card plan and are never
// guessed: those sorts skip the paint (today's path).
//
// value and gain: the server compares the LINE price (LINE_PRICE_SQL: the
// printing's price, else its MTGJSON price, else the NAME's lowest USD
// printing price). A snapshot from a current API carries exactly that as
// `sort_price_usd` (null where the server's is NULL, sorted as -1), and
// OMITS the key on a line whose exact price is not whole cents in [0, 1e9).
// So a value or gain page is built ONLY when every line carries the key;
// otherwise (an older API, or any precision omission) there is no value or
// gain page and those sorts take today's path. `unit_price_usd` (the
// printing's own price, rounded) is never used to order. Gain is computed
// and compared in BigInt cents: (price - paid) x qty can exceed 2^53.
import { STORE } from './offline.codes'
import { ownerGet } from './offlineStore'
import type { SnapshotLine, SnapshotOk } from './snapshots'
import type { CopiesPage, CopiesSummary, CopyItem } from '../../api/collectionCopies'
import type { CopyCondition, CopyFinish, CopySort, CopyStatus, SortDir } from '../../api/collectionCopies.codes'

/** The list's page size (hooks/useCopies COPIES_PAGE_SIZE; a test pins the two together). */
export const FIRST_PAGE_LIMIT = 100
/** v1 of the record (a record of another version is ignored). */
// 2: value / gain from `sort_price_usd` (a v1 record is never read; a 304
// rebuilds it).
export const FIRST_PAGE_SCHEMA = 2
const KEY_PREFIX = 'first_page:'

/** The sorts a snapshot answers with the server's order. */
export const PROVISIONAL_SORTS: readonly CopySort[] = ['added', 'name', 'value', 'set', 'gain', 'qty']

export interface FirstPageSort {
  sort: CopySort
  dir: SortDir
}

export interface StoredFirstPages {
  schema_version: number
  /** The owner database it was written to (re-checked on read). */
  owner_key: string
  /** The owner incarnation and fence epoch it was written under (re-checked on read). */
  owner_incarnation: number
  fence_epoch: number
  collection_id: string
  /** The snapshot it was computed from. */
  etag: string | null
  as_of: string
  copies_rev: number
  total: number
  matched_qty: number
  summary: CopiesSummary
  /** `${sort}:${dir}` -> the first FIRST_PAGE_LIMIT lines in that order. */
  pages: Record<string, CopyItem[]>
}

/** The owner fence a record was written under. */
export interface FirstPageFence {
  ownerKey: string
  incarnation: number
  fenceEpoch: number
}

/** What the screen paints: one page, for exactly this sort. */
export interface ProvisionalFirstPage extends FirstPageSort {
  collectionId: string
  /** The fence it was read under: the page is dropped the moment it moves. */
  fence: FirstPageFence
  asOf: string
  page: CopiesPage
}

export function firstPageRecordKey(collectionId: string): string {
  return KEY_PREFIX + collectionId
}

function sortId(s: FirstPageSort): string {
  return `${s.sort}:${s.dir}`
}

export function isProvisionalSort(sort: CopySort): boolean {
  return PROVISIONAL_SORTS.includes(sort)
}

// ---- The server's order ---------------------------------------------------------------------

type KeyPart = number | bigint | string | boolean

/** RFC 3339 to microseconds since the epoch (the server compares datetimes, never their text). */
function isoMicros(iso: string | null | undefined): number {
  if (!iso) return Number.NEGATIVE_INFINITY
  const frac = /\.(\d+)/.exec(iso)
  const base = Date.parse(frac ? iso.replace(frac[0], '') : iso)
  if (!Number.isFinite(base)) return Number.NEGATIVE_INFINITY
  const micros = frac ? parseInt(frac[1].padEnd(6, '0').slice(0, 6), 10) : 0
  return base * 1000 + micros
}

/**
 * A `sort_price_usd` in whole cents, or null: the API only sends whole-cent
 * values (1.5 for 1.50), so anything else is not trusted as exact.
 */
function priceCents(v: number | null | undefined): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v >= 1e9) return null
  const c = Math.round(v * 100)
  return Math.abs(v * 100 - c) < 1e-6 ? c : null
}

/** An exact decimal purchase price ("2.5", "9998000100.01") in BigInt cents; null when not whole cents. */
export function decimalCents(v: string | null | undefined): bigint | null {
  if (typeof v !== 'string') return null
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(v.trim())
  if (!m) return null
  const c = BigInt(m[2]) * 100n + BigInt((m[3] ?? '').padEnd(2, '0'))
  return m[1] ? -c : c
}

/** cn_sort_parts: (no leading integer, the leading integer or 0, the text). */
function cnParts(cn: string | null): KeyPart[] {
  const s = cn ?? ''
  const m = /^[0-9]{1,9}/.exec(s)
  return [m === null, m ? parseInt(m[0], 10) : 0, s]
}

/**
 * Every line carries the server's exact sort price: the key present (null
 * allowed), a present value in whole cents.
 */
export function carriesSortPrice(lines: readonly SnapshotLine[]): boolean {
  return lines.every((l) => {
    if (!Object.prototype.hasOwnProperty.call(l, 'sort_price_usd')) return false
    return l.sort_price_usd === null || priceCents(l.sort_price_usd) !== null
  })
}

/** Every USD (or currency-less) cost basis reads as exact cents. */
function basisExact(lines: readonly SnapshotLine[]): boolean {
  return lines.every(l => !hasUsdBasis(l) || decimalCents(l.purchase_price) !== null)
}

/**
 * The line's gain in BigInt cents (GAIN: (LINE_PRICE - purchase_price) x
 * qty for a USD or currency-less basis); null when the server's is NULL.
 */
export function gainCents(l: SnapshotLine): bigint | null {
  if (!hasUsdBasis(l)) return null
  const unit = priceCents(l.sort_price_usd)
  const paid = decimalCents(l.purchase_price)
  if (unit === null || paid === null) return null
  return (BigInt(unit) - paid) * BigInt(l.qty)
}

/**
 * `_sort_key` for the snapshot-answerable sorts, i.e. build_list_sql's ORDER
 * BY with every key in the request's direction and copy_id last:
 *   value  COALESCE(LINE_PRICE, -1), copy_id (the unit price, never x qty)
 *   gain   (GAIN IS NOT NULL), COALESCE(GAIN, 0), copy_id: unknown gains go
 *          LAST when desc and FIRST when asc
 * value and gain read `sort_price_usd` only (snapshotOrdersExactly gates them).
 */
export function sortKey(l: SnapshotLine, sort: CopySort): KeyPart[] {
  switch (sort) {
    case 'added': return [isoMicros(l.added_at), l.copy_id]
    case 'name': return [l.card_name.toLowerCase(), l.copy_id]
    case 'set': return [l.set ?? '', ...cnParts(l.collector_number), l.copy_id]
    case 'qty': return [l.qty, l.copy_id]
    case 'gain': {
      const g = gainCents(l)
      return [g !== null, g ?? 0n, l.copy_id]
    }
    default: {
      const p = priceCents(l.sort_price_usd)
      return [p ?? -100, l.copy_id]
    }
  }
}

function cmpPart(a: KeyPart, b: KeyPart): number {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Number(a) - Number(b)
  if (typeof a === 'bigint' && typeof b === 'bigint') return a < b ? -1 : a > b ? 1 : 0
  if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : a > b ? 1 : 0
  const x = String(a)
  const y = String(b)
  return x < y ? -1 : x > y ? 1 : 0
}

function cmpKeys(a: KeyPart[], b: KeyPart[]): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const c = cmpPart(a[i], b[i])
    if (c !== 0) return c
  }
  return a.length - b.length
}

/** A USD (or currency-less) cost basis: the line has a gain once it has a price. */
function hasUsdBasis(l: SnapshotLine): boolean {
  return l.purchase_price !== null && l.purchase_price !== undefined && (l.purchase_currency ?? 'USD') === 'USD'
}

/**
 * The snapshot orders `sort` exactly as the server does: every key is the
 * server's. value / gain ONLY when every line carries the exact sort price
 * (and, for gain, every USD basis is exact cents); never from
 * `unit_price_usd`.
 */
export function snapshotOrdersExactly(lines: readonly SnapshotLine[], sort: CopySort): boolean {
  if (!isProvisionalSort(sort)) return false
  if (sort === 'value') return carriesSortPrice(lines)
  if (sort === 'gain') return carriesSortPrice(lines) && basisExact(lines)
  return true
}

/** The lines in the server's list order for one sort (null: a sort the snapshot cannot order exactly). */
export function sortSnapshotLines(lines: readonly SnapshotLine[], s: FirstPageSort): SnapshotLine[] | null {
  if (!snapshotOrdersExactly(lines, s.sort)) return null
  const keyed = lines.map(line => ({ line, key: sortKey(line, s.sort) }))
  const sign = s.dir === 'desc' ? -1 : 1
  keyed.sort((a, b) => sign * cmpKeys(a.key, b.key))
  return keyed.map(k => k.line)
}

// ---- The record -----------------------------------------------------------------------------

export function itemOfLine(l: SnapshotLine): CopyItem {
  return {
    copy_id: l.copy_id,
    card_name: l.card_name,
    name_key: l.name_key,
    scryfall_id: l.scryfall_id,
    set: l.set,
    set_name: l.set_name,
    collector_number: l.collector_number,
    rarity: l.rarity,
    finish: l.finish as CopyFinish,
    condition: (l.condition as CopyCondition | null) ?? null,
    language: l.language,
    status: l.status as CopyStatus,
    qty: l.qty,
    purchase_price: l.purchase_price,
    purchase_currency: l.purchase_currency,
    source: l.source,
    added_at: l.added_at ?? '',
    // The list shows the LINE price (the server's serialization): the sort
    // price when the snapshot carries it.
    unit_price_usd: Object.prototype.hasOwnProperty.call(l, 'sort_price_usd') ? (l.sort_price_usd ?? null) : l.unit_price_usd,
    image_normal: l.image_normal,
    type_line: l.type_line,
    colors: l.colors,
    color_identity: l.color_identity,
    mv: l.mv,
    binder_id: l.binder_id,
  }
}

/**
 * The whole-collection counts the list's first page carries (SUMMARY_SQL):
 * qty per status, owned + trade, distinct real cards. No money: the want
 * price is the server's (name pricing) and is left to the live page, and so
 * is last_changed_at (the header says "Updating..." in its place).
 */
export function summaryOf(lines: readonly SnapshotLine[]): CopiesSummary {
  const by = { owned: 0, trade: 0, proxy: 0, want: 0 }
  const unique = new Set<string>()
  for (const l of lines) {
    if (l.status === 'owned' || l.status === 'trade' || l.status === 'proxy' || l.status === 'want') by[l.status] += l.qty
    if (l.status === 'owned' || l.status === 'trade') unique.add(l.name_key)
  }
  return { ...by, size_cards: by.owned + by.trade, size_unique: unique.size, last_changed_at: null }
}

/** The record for one stored snapshot body: one page per wanted (answerable) sort. */
export function buildFirstPages(args: {
  fence: FirstPageFence
  body: SnapshotOk
  etag: string | null
  sorts: readonly FirstPageSort[]
}): StoredFirstPages {
  const { body } = args
  const pages: Record<string, CopyItem[]> = {}
  for (const s of args.sorts) {
    const id = sortId(s)
    if (pages[id]) continue
    const sorted = sortSnapshotLines(body.lines, s)
    if (sorted) pages[id] = sorted.slice(0, FIRST_PAGE_LIMIT).map(itemOfLine)
  }
  return {
    schema_version: FIRST_PAGE_SCHEMA,
    owner_key: args.fence.ownerKey,
    owner_incarnation: args.fence.incarnation,
    fence_epoch: args.fence.fenceEpoch,
    collection_id: body.collection_id,
    etag: args.etag,
    as_of: body.as_of,
    copies_rev: body.copies_rev,
    total: body.lines.length,
    matched_qty: body.lines.reduce((n, l) => n + l.qty, 0),
    summary: summaryOf(body.lines),
    pages,
  }
}

/** The record was written under exactly this fence. */
export function writtenUnder(rec: StoredFirstPages, fence: FirstPageFence): boolean {
  return rec.owner_key === fence.ownerKey && rec.owner_incarnation === fence.incarnation && rec.fence_epoch === fence.fenceEpoch
}

/**
 * The record already holds every wanted answerable sort for this snapshot,
 * under this fence. (A sort the snapshot cannot order exactly is never
 * stored, and needs no rebuild: rebuilding would skip it again.)
 */
export function coversSorts(
  rec: StoredFirstPages | null, etag: string | null, body: SnapshotOk, sorts: readonly FirstPageSort[], fence: FirstPageFence,
): boolean {
  if (!rec || rec.schema_version !== FIRST_PAGE_SCHEMA || rec.etag !== etag || rec.copies_rev !== body.copies_rev) return false
  if (!writtenUnder(rec, fence)) return false
  return sorts.every(s => !snapshotOrdersExactly(body.lines, s.sort) || !!rec.pages[sortId(s)])
}

/** The wanted sorts, de-duplicated and limited to what a snapshot answers. */
export function answerableSorts(sorts: readonly FirstPageSort[]): FirstPageSort[] {
  const seen = new Set<string>()
  const out: FirstPageSort[] = []
  for (const s of sorts) {
    if (!isProvisionalSort(s.sort) || seen.has(sortId(s))) continue
    seen.add(sortId(s))
    out.push(s)
  }
  return out
}

/**
 * The stored record (a read: never fenced, the caller checks the owner around
 * it). With `fence`, only a record written under exactly that fence.
 */
export async function readFirstPageRecord(ownerKey: string, collectionId: string, fence?: FirstPageFence): Promise<StoredFirstPages | null> {
  const rec = await ownerGet<StoredFirstPages>(ownerKey, STORE.collection, firstPageRecordKey(collectionId))
  if (!rec || rec.schema_version !== FIRST_PAGE_SCHEMA || rec.owner_key !== ownerKey || rec.collection_id !== collectionId) return null
  if (typeof rec.owner_incarnation !== 'number' || typeof rec.fence_epoch !== 'number') return null
  if (fence && !writtenUnder(rec, fence)) return null
  if (!rec.pages || typeof rec.pages !== 'object') return null
  return rec
}

/** One provisional page for exactly (sort, dir), or null (never another sort's page). */
export function firstPageFor(rec: StoredFirstPages, s: FirstPageSort): ProvisionalFirstPage | null {
  const items = rec.pages[sortId(s)]
  if (!Array.isArray(items)) return null
  return {
    sort: s.sort,
    dir: s.dir,
    collectionId: rec.collection_id,
    fence: { ownerKey: rec.owner_key, incarnation: rec.owner_incarnation, fenceEpoch: rec.fence_epoch },
    asOf: rec.as_of,
    page: {
      items,
      // Never a server cursor: the provisional page does not page.
      next_cursor: null,
      total: rec.total,
      matched_qty: rec.matched_qty,
      copies_rev: rec.copies_rev,
      summary: rec.summary,
    },
  }
}
